import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Initial state
const initialState = {
  theme: 'light', // light or dark
  primaryColor: '#1e40af', // Default blue
  fontSize: 'medium', // small, medium, large
  fontFamily: 'Roboto' // Roboto, Arial, etc.
};

// Action types
const actionTypes = {
  SET_THEME: 'SET_THEME',
  SET_PRIMARY_COLOR: 'SET_PRIMARY_COLOR',
  SET_FONT_SIZE: 'SET_FONT_SIZE',
  SET_FONT_FAMILY: 'SET_FONT_FAMILY',
  RESET_THEME: 'RESET_THEME'
};

// Reducer
const themeReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_THEME:
      return {
        ...state,
        theme: action.payload
      };
    case actionTypes.SET_PRIMARY_COLOR:
      return {
        ...state,
        primaryColor: action.payload
      };
    case actionTypes.SET_FONT_SIZE:
      return {
        ...state,
        fontSize: action.payload
      };
    case actionTypes.SET_FONT_FAMILY:
      return {
        ...state,
        fontFamily: action.payload
      };
    case actionTypes.RESET_THEME:
      return initialState;
    default:
      return state;
  }
};

// Context
const ThemeContext = createContext();

// Provider
export const ThemeProvider = ({ children }) => {
  const [state, dispatch] = useReducer(themeReducer, () => {
    // Load theme from localStorage or use initial state
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      try {
        return JSON.parse(savedTheme);
      } catch (error) {
        console.error('Error parsing saved theme:', error);
      }
    }
    return initialState;
  });

  // Save theme to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('theme', JSON.stringify(state));
  }, [state]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Set theme class
    if (state.theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Set primary color
    root.style.setProperty('--color-primary', state.primaryColor);
    
    // Set font size
    const sizeMap = {
      small: '0.875rem',
      medium: '1rem',
      large: '1.125rem'
    };
    root.style.fontSize = sizeMap[state.fontSize] || sizeMap.medium;
    
    // Set font family
    root.style.fontFamily = state.fontFamily;
  }, [state]);

  // Actions
  const setTheme = (theme) => {
    dispatch({ type: actionTypes.SET_THEME, payload: theme });
  };

  const setPrimaryColor = (color) => {
    dispatch({ type: actionTypes.SET_PRIMARY_COLOR, payload: color });
  };

  const setFontSize = (size) => {
    dispatch({ type: actionTypes.SET_FONT_SIZE, payload: size });
  };

  const setFontFamily = (family) => {
    dispatch({ type: actionTypes.SET_FONT_FAMILY, payload: family });
  };

  const resetTheme = () => {
    dispatch({ type: actionTypes.RESET_THEME });
  };

  const value = {
    ...state,
    setTheme,
    setPrimaryColor,
    setFontSize,
    setFontFamily,
    resetTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;